import { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from '@eluve/components';
import { formatTo12HourTime } from '@eluve/date-utils';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { useTranscriptionMachineSelector } from '../hooks/useTranscriptionMachine';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Humanize an elapsed duration as a relative "ago" phrase that scales:
 * seconds → minutes → hours, and caps at "more than a day ago" beyond 24h.
 * Deliberately coarse — conveys scale, not exact duration. Recomputed live so
 * it stays current while the dialog is open.
 *
 * Note: intentionally NOT `timestampToHumanReadable` from `@eluve/date-utils` —
 * that helper says "just now" under a minute (we want second-level granularity
 * here, e.g. "about 13 seconds ago") and is uncapped at days (we cap at "more
 * than a day ago"), both per the ENG-4498 product requirement.
 */
const formatAgo = (ms: number): string => {
  if (ms >= DAY_MS) {
    return 'more than a day ago';
  }
  const seconds = Math.max(1, Math.round(ms / 1000));
  if (seconds < 60) {
    return `about ${seconds} second${seconds === 1 ? '' : 's'} ago`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `about ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  const hours = Math.round(minutes / 60);
  return `about ${hours} hour${hours === 1 ? '' : 's'} ago`;
};

/**
 * [ENG-4498] After a session ends, warn the user when a recording segment
 * captured no audio (typically the device went to sleep / the tab was
 * suspended). This is informational — not a hard error — and it doubles as
 * education on how to avoid the problem next time. Mirrors the
 * `RecordingFailedDialog` pattern, including the PostHog + Datadog telemetry so
 * we can measure how often this is shown and how much audio time was lost.
 */
export const AudioPotentiallyLostDialog = () => {
  const logger = useNamedLogger('AudioPotentiallyLostDialog');
  const captureEvent = useCaptureEvent();
  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromParams();

  const audioPotentiallyLost = useTranscriptionMachineSelector(
    (s) => s.context.audioPotentiallyLost,
  );

  const [dismissed, setDismissed] = useState(false);
  const open = Boolean(audioPotentiallyLost) && !dismissed;

  // Tick once a second while open so the "ago" phrase stays live (the user is
  // looking at a relative time that should keep counting up, not a frozen value).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!open) {
      return;
    }
    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [open]);

  useEffect(() => {
    // Reset the dismissed flag when a fresh loss is recorded (new errorDetectedAt).
    setDismissed(false);
  }, [audioPotentiallyLost?.errorDetectedAt]);

  useEffect(() => {
    if (open && audioPotentiallyLost) {
      const telemetry = {
        appointmentId: audioPotentiallyLost.appointmentId,
        segmentId: audioPotentiallyLost.segmentId,
        lostWindowMs: audioPotentiallyLost.lostWindowMs,
        recordingStartedAt: audioPotentiallyLost.recordingStartedAt,
        lastKnownGoodHealthCheckAt:
          audioPotentiallyLost.lastKnownGoodHealthCheckAt ?? null,
        errorDetectedAt: audioPotentiallyLost.errorDetectedAt,
        userId,
        tenantId,
      };
      // Datadog (via centralized logger) — frequency + lost-duration monitor.
      logger.warn('Audio potentially lost notice shown to user', telemetry);
      // PostHog — how often we present this + the lost-duration distribution.
      captureEvent('appointment_audio_potentially_lost', telemetry);
    }
  }, [open, audioPotentiallyLost, captureEvent, logger, userId, tenantId]);

  if (!open || !audioPotentiallyLost) {
    return null;
  }

  // When audio had been flowing and then stopped, anchor "ago" to the last
  // moment we heard audio (the sleep gap). When audio never flowed, anchor to
  // the recording start. Both are recomputed against `now` so they stay live.
  const hadAudioThenStopped = Boolean(
    audioPotentiallyLost.lastKnownGoodHealthCheckAt,
  );
  const anchorIso =
    audioPotentiallyLost.lastKnownGoodHealthCheckAt ??
    audioPotentiallyLost.recordingStartedAt;
  const ago = formatAgo(Math.max(0, now - new Date(anchorIso).getTime()));
  const clock = formatTo12HourTime(anchorIso);
  const agoWithClock = clock ? `${ago} (at ${clock})` : ago;

  return (
    <Dialog open={open} onOpenChange={(next) => setDismissed(!next)}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="text-bg-warning-strong-primary">
            <Icon name="TriangleAlert" size="xl" className="mr-4 inline-flex" />
            This appointment may be missing audio
          </DialogTitle>
          <DialogDescription className="pt-2">
            {hadAudioThenStopped
              ? `The last audio we received was ${agoWithClock} — it looks like the device went to sleep or the browser was suspended after that. Any speech during that time was not captured and can't be recovered.`
              : `We started recording ${agoWithClock} but haven't received any audio since — this usually happens when the device goes to sleep or the browser is suspended. If you were speaking, that audio was not captured and can't be recovered.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm text-text-secondary">
          <p className="font-medium text-text-primary">
            To avoid this next time:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-left">
            <li>
              <span className="font-medium">On a computer:</span> keep this
              browser tab open and in the foreground while recording —
              don&apos;t switch to another tab or let the computer sleep.
            </li>
            <li>
              <span className="font-medium">On a phone or tablet:</span> keep
              the browser open and turn off auto-lock / screen lock so the
              screen doesn&apos;t turn off while recording.
            </li>
          </ul>
        </div>
        <DialogFooter>
          <Button onClick={() => setDismissed(true)}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
